import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ListChecks, MonitorPlay, ClipboardList, ShieldCheck, RefreshCw, CalendarDays } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, fetchConfig } from "@/lib/api";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { PlaybookView } from "@/components/PlaybookView";
import { CustomPlaysSheet } from "@/components/coach/CustomPlaysSheet";
import { copyJoin, copyStaff } from "@/components/coach/TeamCard";
import { AccessBanner } from "@/components/coach/AccessBanner";
import { useAuth } from "@/context/AuthContext";
import { exportPlaysPdf } from "@/lib/pdf";
import { exportWristband } from "@/lib/wristband";
import { toast } from "sonner";

export default function TeamPlaybookPage() {
  const { teamId } = useParams();
  const [config, setConfig] = useState(null);
  const [team, setTeam] = useState(null);
  const [error, setError] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [jump, setJump] = useState(null);
  const [version, setVersion] = useState(0);
  const { user } = useAuth();
  const canEdit = user.access.has_access;

  useEffect(() => { fetchConfig().then(setConfig); }, []);
  useEffect(() => {
    api.team(teamId).then(setTeam).catch(() => setError(true));
  }, [teamId, version]);

  const loadPlays = useCallback((s) => api.teamPlays(teamId, s), [teamId]);
  const onSave = useCallback(async (body) => { const p = await api.savePlay(teamId, body); setVersion((v) => v + 1); return p; }, [teamId]);
  const onReset = useCallback(async (key) => { const p = await api.resetPlay(teamId, key); setVersion((v) => v + 1); return p; }, [teamId]);

  if (error) return <PageShell><AppHeader /><div className="card" data-testid="team-not-found">Team not found. <Link to="/coach" className="underline">Back to teams</Link></div></PageShell>;
  if (!config || !team) return <div className="min-h-screen grid place-items-center font-heading text-2xl">Loading playbook...</div>;
  const cfg = config.sports[team.sport][team.level];
  const exportPdf = async (items, name) => {
    const t = toast.loading("Building PDF...");
    try {
      await exportPlaysPdf(items, cfg, team.name, name);
      toast.success("PDF downloaded", { id: t });
    } catch {
      toast.error("Could not build the PDF", { id: t });
    }
  };
  const rotate = async () => {
    try {
      const { staff_code } = await api.rotateStaffCode(teamId);
      setTeam((t) => ({ ...t, staff_code }));
      toast.success(`New coach code: ${staff_code}`);
    } catch {
      toast.error("Could not make a new coach link");
    }
  };
  const wristband = async (plays, pos) => {
    const t = toast.loading("Building wristband cards...");
    try {
      await exportWristband(plays, cfg, team.name, pos);
      toast.success("Wristband PDF downloaded", { id: t });
    } catch {
      toast.error("Could not build the wristband", { id: t });
    }
  };

  return (
    <PageShell>
      <AppHeader>
        <Link to="/coach" className="nav-btn" data-testid="back-to-teams-link"><ArrowLeft size={18} /> Teams</Link>
      </AppHeader>
      <div className="team-banner" data-testid="team-banner">
        <div className="min-w-0">
          <p className="ctl-label">Team playbook · {cfg.name}</p>
          <h1 className="font-heading text-2xl sm:text-4xl font-extrabold uppercase tracking-tight truncate" data-testid="team-name">{team.name}</h1>
        </div>
        <div className="flex gap-2">
          <button className="btn-ghost !px-3" data-testid="banner-copy-join-button" onClick={() => copyJoin(team.join_code)}>
            <Copy size={18} /> <span className="font-mono tracking-widest">{team.join_code}</span>
          </button>
          <button className="btn-ghost !px-3" data-testid="open-custom-plays-button" onClick={() => setSheet(true)}>
            <ListChecks size={18} /> Custom ({team.custom_count})
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2" data-testid="coach-tools">
        <Link to={`/coach/teams/${teamId}/gameday`} className="btn-ghost !text-base" data-testid="open-gameday-link"><MonitorPlay size={18} /> Game day</Link>
        <Link to={`/coach/teams/${teamId}/board`} className="btn-ghost !text-base" data-testid="open-board-link"><CalendarDays size={18} /> Team board</Link>
        <Link to={`/coach/teams/${teamId}/lineups`} className="btn-ghost !text-base" data-testid="open-lineups-link"><ClipboardList size={18} /> Lineups</Link>
        <button className="btn-ghost !text-base" data-testid="copy-staff-link-button" onClick={() => copyStaff(team.staff_code)}>
          <ShieldCheck size={18} /> Coach link <span className="font-mono tracking-widest" data-testid="staff-code-text">{team.staff_code}</span>
        </button>
        {canEdit && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button className="btn-ghost !text-base" data-testid="rotate-staff-link-button"><RefreshCw size={18} /> New coach link</button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="rotate-staff-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Make a new coach link?</AlertDialogTitle>
                <AlertDialogDescription>The old coach link and code stop working right away. Share the new one with your coaches. Player links aren't affected.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="rotate-staff-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="rotate-staff-confirm" onClick={rotate}>Make new link</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      {!canEdit && <AccessBanner access={user.access} />}
      <PlaybookView cfg={cfg} loadPlays={loadPlays} editable={canEdit} onSave={onSave} onReset={onReset} jump={jump}
        onExportPdf={canEdit ? (play, ball) => exportPdf([{ play, ball }], `${team.name}-${play.fielder}-play.pdf`) : undefined}
        onAssign={canEdit ? (play, ball) => api.assign(teamId, { key: play.key, ball }).then(() => toast.success("Assigned to practice for the whole team"), () => toast.error("Could not assign")) : undefined} />
      <CustomPlaysSheet open={sheet} onOpenChange={setSheet} teamId={teamId} version={version}
        onExport={canEdit ? (plays) => exportPdf(plays.map((play) => ({ play })), `${team.name}-playbook.pdf`) : undefined}
        onWristband={canEdit ? (plays, pos) => wristband(plays, pos) : undefined}
        onPick={(p) => setJump({ outs: p.outs, runners: p.runners, hitType: p.hit_type, tap: p.ball, n: Date.now() })} />
    </PageShell>
  );
}
